import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHead, Btn, Card, Field, Toggle, ImageUpload, Label, AiCopyButton } from "@/admin/ui";
import { toast } from "sonner";
import { ChevronUp, ChevronDown } from "lucide-react";

// which fields to expose per section type
const FIELDS = {
  hero: ["overline", "heading", "subheading", "cta_label", "cta_href", "cta2_label", "cta2_href"],
  drop_story: ["overline", "heading", "copy", "cta_label", "cta_href"],
  brand_statement: ["heading", "copy"],
  ori: ["overline", "heading", "subheading", "copy", "cta_label", "cta_href", "cta2_label", "cta2_href"],
  destinations: ["heading"],
  culture: ["overline", "heading"],
  complete_look: ["overline", "heading", "product_slug", "accessory_slug"],
  lifestyle: ["overline", "heading"],
  founder: ["overline", "heading", "copy", "signature"],
  community: ["overline", "heading", "copy"],
  private_access: ["overline", "heading", "copy", "cta_label"],
};
const IMG_FIELDS = { hero: ["image_desktop", "image_mobile"], brand_statement: ["image_desktop"], ori: ["image_desktop"], founder: ["image_desktop"] };

const HomepageAdmin = () => {
  const [sections, setSections] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/admin/homepage").then((r) => setSections((r.data.sections || []).sort((a, b) => a.order - b.order))).catch(() => {}); }, []);

  const save = async () => {
    try { await api.put("/admin/homepage", { sections }); toast.success("Homepage saved"); }
    catch { toast.error("Save failed"); }
  };
  const upData = (idx, k, v) => setSections((s) => s.map((sec, i) => i === idx ? { ...sec, data: { ...sec.data, [k]: v } } : sec));
  const upItem = (idx, ii, k, v) => setSections((s) => s.map((sec, i) => {
    if (i !== idx) return sec;
    const key = Array.isArray(sec.data.items) ? "items" : "cards";
    const items = [...(sec.data[key] || [])];
    items[ii] = { ...items[ii], [k]: v };
    return { ...sec, data: { ...sec.data, [key]: items } };
  }));
  const upImage = (idx, ii, v) => setSections((s) => s.map((sec, i) => {
    if (i !== idx) return sec;
    const images = [...(sec.data.images || [])];
    images[ii] = v;
    return { ...sec, data: { ...sec.data, images } };
  }));
  const toggleVis = (idx) => setSections((s) => s.map((sec, i) => i === idx ? { ...sec, visible: !sec.visible } : sec));
  const move = (idx, dir) => setSections((s) => {
    const arr = [...s]; const j = idx + dir; if (j < 0 || j >= arr.length) return s;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; return arr.map((x, i) => ({ ...x, order: i + 1 }));
  });

  return (
    <div className="max-w-3xl">
      <PageHead title="Homepage" sub="Modular sections — show/hide, reorder and edit.">
        <Btn onClick={save} testid="admin-save">Publish</Btn>
      </PageHead>
      <div className="space-y-3">
        {sections.map((sec, idx) => (
          <Card key={sec.id} className="!p-0">
            <div className="flex items-center gap-3 px-5 py-3">
              <div className="flex flex-col">
                <button onClick={() => move(idx, -1)} className="text-espresso/40 hover:text-espresso"><ChevronUp size={15} /></button>
                <button onClick={() => move(idx, 1)} className="text-espresso/40 hover:text-espresso"><ChevronDown size={15} /></button>
              </div>
              <div className="flex-1">
                <p className="font-serif text-lg capitalize">{sec.type.replace(/_/g, " ")}</p>
                <p className="text-xs text-espresso/40">{sec.data?.heading}</p>
              </div>
              <Toggle label="" value={sec.visible} onChange={() => toggleVis(idx)}
                testid={`homepage-section-toggle-${sec.type}`} />
              <button onClick={() => setOpen(open === idx ? null : idx)} className="text-[0.66rem] tracking-widest uppercase border border-espresso px-3 py-1.5">{open === idx ? "Close" : "Edit"}</button>
            </div>
            {open === idx && (
              <div className="px-5 pb-5 border-t border-espresso/10 pt-4">
                {(FIELDS[sec.type] || []).map((f) => {
                  const isCopy = !f.endsWith("_href") && !f.endsWith("_slug");
                  return (
                    <div key={f}>
                      <Field label={f.replace(/_/g, " ")} textarea={f === "copy"} value={sec.data?.[f]} onChange={(v) => upData(idx, f, v)} />
                      {isCopy && (
                        <AiCopyButton
                          instruction={`Rewrite the "${f.replace(/_/g, " ")}" text for the homepage ${sec.type.replace(/_/g, " ")} section.`}
                          current={sec.data?.[f]} onResult={(t) => upData(idx, f, t)}
                          testid={`homepage-ai-${sec.type}-${f}`} />
                      )}
                    </div>
                  );
                })}
                {(IMG_FIELDS[sec.type] || []).map((f) => (
                  <ImageUpload key={f} label={f.replace(/_/g, " ")} testid={`homepage-img-${sec.type}-${f}`} value={sec.data?.[f]} onChange={(v) => upData(idx, f, v)} />
                ))}
                {Array.isArray(sec.data?.items || sec.data?.cards) && (sec.data.items || sec.data.cards).map((it, ii) => (
                  <div key={ii} className="border border-espresso/10 p-3 mb-3" data-testid={`homepage-item-${sec.type}-${ii}`}>
                    <p className="text-[0.6rem] uppercase tracking-widest text-espresso/40 mb-2">Item {ii + 1}</p>
                    {"label" in it && <Field label="label" value={it.label} onChange={(v) => upItem(idx, ii, "label", v)} />}
                    {"href" in it && <Field label="link" value={it.href} onChange={(v) => upItem(idx, ii, "href", v)} />}
                    {"copy" in it && <Field label="copy" textarea value={it.copy} onChange={(v) => upItem(idx, ii, "copy", v)} />}
                    <ImageUpload label="image" testid={`homepage-img-${sec.type}-${ii}`} value={it.image} onChange={(v) => upItem(idx, ii, "image", v)} />
                  </div>
                ))}
                {Array.isArray(sec.data?.images) && (
                  <div>
                    <Label>Images</Label>
                    {sec.data.images.map((im, ii) => (
                      <ImageUpload key={ii} label={`image ${ii + 1}`} testid={`homepage-img-${sec.type}-${ii}`} value={im} onChange={(v) => upImage(idx, ii, v)} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default HomepageAdmin;
